import { createContext, useContext, useEffect, useMemo } from "react";
import { useEditor, useEditorState, EditorContent, Node, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Underline, Heading3, List, ListOrdered, Undo2, Redo2, Link2, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

// Editor dei testi del contratto (catalogo servizi). Offre solo la formattazione
// che il PDF sa riprodurre (backend/testo_ricco.py): grassetto, corsivo,
// sottolineato, titoletto, elenchi puntati e numerati. Tutto il resto dello
// StarterKit è spento, così nell'editor non si può scrivere nulla che poi
// sparirebbe dal contratto stampato.
const ESTENSIONI = [
  StarterKit.configure({
    heading: { levels: [3] },
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    link: false,
  }),
];

// ==================== Importi collegati ====================

// Un testo di pagamento scritto a mano può citare gli importi del servizio
// con un segnaposto, salvato come testo `{{chiave}}` (chiave = "attivazione"
// o l'addon_id). Il backend lo risolve coi prezzi del catalogo e, nel
// contratto, con quelli concordati (backend/proposta_economica.py,
// risolvi_segnaposti). Qui è un chip non modificabile: nel campo si converte
// da/verso il testo, perché la pulizia dell'HTML lato server toglie ogni
// attributo e lascerebbe solo il testo.
const SEGNAPOSTO_TESTO = /\{\{([A-Za-z0-9_-]+)\}\}/g;
const SEGNAPOSTO_HTML = /<span[^>]*data-segnaposto="([^"]+)"[^>]*><\/span>/g;
const versoEditor = (html) => (html || "").replace(SEGNAPOSTO_TESTO, '<span data-segnaposto="$1"></span>');
const versoTesto = (html) => html.replace(SEGNAPOSTO_HTML, "{{$1}}");

// chiave → { etichetta, valore } dei segnaposti disponibili, per i chip.
const SegnapostiContext = createContext({});

function ChipSegnaposto({ node, selected }) {
  const voce = useContext(SegnapostiContext)[node.attrs.chiave];
  return (
    <NodeViewWrapper
      as="span"
      className={`inline-flex items-center gap-1 h-[22px] px-[7px] mx-px rounded-[6px] text-[13px] font-semibold align-baseline ${
        voce ? "bg-[#eeeefc] text-[#3f3fb0]" : "bg-[#fdecea] text-[#c0392b]"
      } ${selected ? "ring-2 ring-[#5b5bd6]/40" : ""}`}
      data-testid={`segnaposto-${node.attrs.chiave}`}
    >
      {voce ? (
        <>
          <span className="font-medium text-[#6b6bd0]">{voce.etichetta}</span>
          <span className="scheda-font-mono">{voce.valore}</span>
        </>
      ) : (
        "Voce eliminata"
      )}
    </NodeViewWrapper>
  );
}

const Segnaposto = Node.create({
  name: "segnaposto",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      chiave: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-segnaposto"),
        renderHTML: (attrs) => ({ "data-segnaposto": attrs.chiave }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-segnaposto]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes];
  },
  renderText({ node }) {
    return `{{${node.attrs.chiave}}}`;
  },
  addNodeView() {
    return ReactNodeViewRenderer(ChipSegnaposto);
  },
});
const ESTENSIONI_CON_SEGNAPOSTI = [...ESTENSIONI, Segnaposto];

function BottoneStrumento({ attivo, disabilitato, onClick, titolo, children }) {
  return (
    <button
      type="button"
      title={titolo}
      aria-label={titolo}
      aria-pressed={attivo}
      disabled={disabilitato}
      // mousedown invece di click: non toglie il fuoco all'editor, così la
      // selezione resta dov'era e il comando si applica al testo selezionato.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`h-8 w-8 rounded-[8px] flex items-center justify-center transition-colors disabled:opacity-35 ${
        attivo ? "bg-[#eeeefc] text-[#5b5bd6]" : "text-[#565d6d] hover:bg-[#eeeefc] hover:text-[#5b5bd6]"
      }`}
    >
      {children}
    </button>
  );
}

// `alto`: editor che occupa da solo una sezione della modale. Cresce col
// testo invece di scorrere: scorre già il pannello che lo contiene.
// `segnaposti`: [{ chiave, etichetta, valore }] degli importi che il testo può
// citare; se presente compare il menu «Importo». Va passato fin dal primo
// render: le estensioni dell'editor si fissano alla creazione.
export default function EditorTestoRicco({ value, onChange, testId, alto = false, segnaposti = null }) {
  const conSegnaposti = Boolean(segnaposti);
  const leggi = (ed) => (ed.isEmpty ? "" : conSegnaposti ? versoTesto(ed.getHTML()) : ed.getHTML());
  const editor = useEditor({
    extensions: conSegnaposti ? ESTENSIONI_CON_SEGNAPOSTI : ESTENSIONI,
    content: conSegnaposti ? versoEditor(value) : value || "",
    onUpdate: ({ editor: ed }) => onChange(leggi(ed)),
  });
  const perChiave = useMemo(
    () => Object.fromEntries((segnaposti || []).map((s) => [s.chiave, s])),
    [segnaposti],
  );

  // Il valore può cambiare da fuori (apertura di un altro servizio senza
  // smontare il componente): si riallinea l'editor, ma mai mentre l'utente
  // scrive, altrimenti il cursore salterebbe a ogni tasto.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    if ((value || "") !== leggi(editor)) {
      editor.commands.setContent(conSegnaposti ? versoEditor(value) : value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  const stato = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed && {
      bold: ed.isActive("bold"),
      italic: ed.isActive("italic"),
      underline: ed.isActive("underline"),
      titoletto: ed.isActive("heading", { level: 3 }),
      puntato: ed.isActive("bulletList"),
      numerato: ed.isActive("orderedList"),
      annulla: ed.can().undo(),
      ripeti: ed.can().redo(),
    },
  });

  if (!editor || !stato) return null;
  const catena = () => editor.chain().focus();
  const icona = "w-[17px] h-[17px]";

  return (
    <div
      className={`editor-testo-ricco ${alto ? "editor-testo-ricco--alto" : ""} rounded-[14px] overflow-hidden border border-[#e4e4ed] bg-white transition-colors focus-within:border-[#5b5bd6] focus-within:shadow-[0_0_0_3px_rgba(91,91,214,.14)]`}
      data-testid={testId}
    >
      <div className="flex items-center gap-0.5 h-[46px] px-2.5 border-b border-[#eeeef5] bg-[#fbfbfd]">
        <BottoneStrumento titolo="Grassetto" attivo={stato.bold} onClick={() => catena().toggleBold().run()}>
          <Bold className={icona} strokeWidth={2.2} />
        </BottoneStrumento>
        <BottoneStrumento titolo="Corsivo" attivo={stato.italic} onClick={() => catena().toggleItalic().run()}>
          <Italic className={icona} strokeWidth={2} />
        </BottoneStrumento>
        <BottoneStrumento titolo="Sottolineato" attivo={stato.underline} onClick={() => catena().toggleUnderline().run()}>
          <Underline className={icona} strokeWidth={2} />
        </BottoneStrumento>
        <span className="w-px h-5 bg-[#e4e4ed] mx-1.5" />
        <BottoneStrumento titolo="Titoletto" attivo={stato.titoletto} onClick={() => catena().toggleHeading({ level: 3 }).run()}>
          <Heading3 className={icona} strokeWidth={2} />
        </BottoneStrumento>
        <BottoneStrumento titolo="Elenco puntato" attivo={stato.puntato} onClick={() => catena().toggleBulletList().run()}>
          <List className={icona} strokeWidth={2} />
        </BottoneStrumento>
        <BottoneStrumento titolo="Elenco numerato" attivo={stato.numerato} onClick={() => catena().toggleOrderedList().run()}>
          <ListOrdered className={icona} strokeWidth={2} />
        </BottoneStrumento>
        {conSegnaposti && (
          <>
            <span className="w-px h-5 bg-[#e4e4ed] mx-1.5" />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="h-8 px-2 rounded-[8px] flex items-center gap-1.5 text-[12.5px] font-semibold text-[#565d6d] hover:bg-[#eeeefc] hover:text-[#5b5bd6] transition-colors"
                  data-testid={testId ? `${testId}-importo` : undefined}
                >
                  <Link2 className="w-[15px] h-[15px]" strokeWidth={2} />
                  Importo
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[220px]">
                {segnaposti.map((s) => (
                  <DropdownMenuItem
                    key={s.chiave}
                    onSelect={() => catena().insertContent({ type: "segnaposto", attrs: { chiave: s.chiave } }).run()}
                    className="flex items-center justify-between gap-4"
                    data-testid={testId ? `${testId}-importo-${s.chiave}` : undefined}
                  >
                    <span>{s.etichetta}</span>
                    <span className="text-[12px] text-[#6b7280] scheda-font-mono">{s.valore}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
        <span className="flex-1" />
        <BottoneStrumento titolo="Annulla" disabilitato={!stato.annulla} onClick={() => catena().undo().run()}>
          <Undo2 className={icona} strokeWidth={2} />
        </BottoneStrumento>
        <BottoneStrumento titolo="Ripeti" disabilitato={!stato.ripeti} onClick={() => catena().redo().run()}>
          <Redo2 className={icona} strokeWidth={2} />
        </BottoneStrumento>
      </div>
      <SegnapostiContext.Provider value={perChiave}>
        <EditorContent editor={editor} />
      </SegnapostiContext.Provider>
    </div>
  );
}
